import { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "@/lib/api";
import GiftModal from "@/components/GiftModal";

const COLORS = ["#ff5577", "#ffaa00", "#66cc66", "#33aaff", "#aa77ff", "#ff66cc", "#ffffff"];
const PAGE_SIZE = 8;
// V2.6.8 — Page 2 holds the three "reaction" badges + V2.8.8 new emotion badges.
const PAGE_2_IDS = ["badge_trump", "badge_fullport", "badge_blowup",
                    "badge_break_even", "badge_stressed", "badge_stand_proud"];

export default function StoreTab() {
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState({ owned: [], name_color: null });
  const [page, setPage] = useState(1);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [giftSku, setGiftSku] = useState(null);

  useEffect(() => { reload(); checkPendingSession(); }, []);
  async function reload() {
    try {
      const [a, b] = await Promise.all([api.get("/store/catalog"), api.get("/store/me")]);
      setItems(a.data); setMine(b.data);
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function checkPendingSession() {
    // V2.6.9 — when Stripe redirects back here with ?session_id=..., poll status
    // a few times until payment_status flips to 'paid', then refresh inventory.
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) return;
    setMsg("Confirming your purchase…");
    for (let i = 0; i < 5; i++) {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setMsg(`Purchase complete! ${data.sku || ""} added to your account.`);
          await reload();
          break;
        }
      } catch {}
      await new Promise(r => setTimeout(r, 1500));
    }
    // Strip the query string regardless of result
    window.history.replaceState({}, "", window.location.pathname + "?tab=store");
    setTimeout(() => setMsg(""), 4000);
  }
  async function buy(sku) {
    try {
      const { data } = await api.post("/store/purchase", { sku });
      // V2.6.9 — non-Owner real-billing path returns a Stripe Checkout URL.
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setMsg(data.message || "Purchased!");
      setTimeout(() => setMsg(""), 3500);
      await reload();
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); setTimeout(() => setErr(""), 4500); }
  }
  async function setColor(color) {
    try { await api.put("/store/name-color", { color }); setMsg(`Color set to ${color}`); setTimeout(() => setMsg(""), 2000); await reload(); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); setTimeout(() => setErr(""), 3000); }
  }

  // Split items: Page 2 = the three reaction badges; Page 1 = everything else,
  // capped at 8 (any overflow spills into Page 2 after the reactions).
  const { page1, page2 } = useMemo(() => {
    const setP2 = new Set(PAGE_2_IDS);
    const rest = items.filter(i => !setP2.has(i.id));
    const reactions = items.filter(i => setP2.has(i.id));
    const p1 = rest.slice(0, PAGE_SIZE);
    const overflow = rest.slice(PAGE_SIZE);
    return { page1: p1, page2: [...reactions, ...overflow] };
  }, [items]);

  const visible = page === 1 ? page1 : page2;
  const totalPages = page2.length > 0 ? 2 : 1;

  return (
    <div data-testid="store-tab" className="space-y-4 max-w-2xl">
      {msg && <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1.5">{msg}</div>}
      {err && <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">{err}</div>}
      <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
        <i className="fa-solid fa-gift mr-1"/> Buy items for yourself or click <b>Gift</b> to send them to a friend.
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {visible.map(it => {
          const owned = mine.owned.includes(it.id);
          return (
            <div key={it.id} data-testid={`store-card-${it.id}`} className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 flex gap-3">
              <div className="w-16 h-16 flex-shrink-0 bg-slate-900/60 border border-slate-800 rounded-lg flex items-center justify-center overflow-hidden">
                {it.image_url
                  ? <img src={it.image_url} alt={it.name} className="w-12 h-12 object-contain"/>
                  : <i className={`fa-solid ${it.id === "color_name" ? "fa-palette text-pink-300" :
                                              it.id === "music_upload" ? "fa-music text-amber-300" :
                                              it.id === "rename_item" ? "fa-pen-to-square text-blue-300" :
                                              it.id === "custom_execution" ? "fa-volume-high text-emerald-300" :
                                              "fa-gem text-sky-300"} text-2xl`}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white">{it.name}</div>
                <div className="text-[11px] text-slate-400 mt-1 min-h-[2.5rem]">{it.description}</div>
                <div className="mt-2 flex items-center justify-between">
                  <div className="text-amber-300 font-black">${it.price_usd}</div>
                  <div className="flex gap-1.5">
                    <button data-testid={`store-buy-${it.id}`} onClick={() => buy(it.id)} disabled={owned && !["rename_item"].includes(it.id)}
                      className={`px-3 py-1.5 text-[10px] font-bold rounded ${owned && !["rename_item"].includes(it.id) ? "bg-emerald-600/30 text-emerald-200" : "bg-amber-500 hover:bg-amber-400 text-slate-900"}`}>
                      {owned && !["rename_item"].includes(it.id) ? "Owned" : "Buy"}
                    </button>
                    <button data-testid={`store-gift-${it.id}`} onClick={() => setGiftSku(it.id)}
                      title="Gift to a friend"
                      className="px-2.5 py-1.5 text-[10px] font-bold rounded bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-200">
                      <img src="/ui/gift.png" alt="Gift" className="w-3.5 h-3.5 inline-block -mt-0.5 mr-1"/>Gift
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="store-pagination">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} data-testid="store-prev"
            className="px-3 py-1.5 text-[11px] font-bold rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300">
            <i className="fa-solid fa-chevron-left mr-1"/> Prev
          </button>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => setPage(n)} data-testid={`store-page-${n}`}
                className={`w-7 h-7 rounded text-[11px] font-bold ${page === n ? "bg-amber-500 text-slate-900" : "bg-slate-800 text-slate-400 hover:text-white"}`}>
                {n}
              </button>
            ))}
          </div>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="store-next"
            className="px-3 py-1.5 text-[11px] font-bold rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300">
            Next <i className="fa-solid fa-chevron-right ml-1"/>
          </button>
        </div>
      )}

      {mine.owned.includes("color_name") && (
        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
          <div className="text-[10px] uppercase font-bold text-slate-500 mb-2">Pick your name color</div>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(c => (
              <button key={c} data-testid={`color-${c.slice(1)}`} onClick={() => setColor(c)}
                className={`w-8 h-8 rounded-full border-2 ${mine.name_color === c ? "border-white" : "border-slate-700"}`}
                style={{ background: c }}/>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 mt-2">Active: <span style={{ color: mine.name_color || "#fff" }}>{mine.name_color || "default"}</span></div>
        </div>
      )}
      {giftSku && <GiftModal initialSku={giftSku} onClose={() => setGiftSku(null)} onSent={() => { setGiftSku(null); reload(); }}/>}
    </div>
  );
}
